import type { AxiosInstance } from 'axios';
import axios from 'axios';

import type {
  ApiResponse,
  Availability,
  Booking,
  Brand,
  CheckAvailabilityRequest,
  ChooseTreatmentRequest,
  ChooseTreatmentResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  CustomerPackage,
  CustomerPackageStatus,
  DeleteTreatmentRequest,
  DeleteTreatmentResponse,
  GetCustomerPackagesStatusRequest,
  ListTreatmentsRequest,
  OTPResponse,
  QrPaymentStatus,
  Therapist,
  Treatment,
  ValidateBookingRequest,
  ValidateBookingResponse,
  ValidateCustomerPackagesRequest,
  ValidatePackagesResponse,
} from '@/models';

import { STORAGE_KEYS } from './storageKeys';

export { STORAGE_KEYS };

class BookingApiClient {
  private client: AxiosInstance;

  constructor() {
    // Get base URL from environment variables
    const baseURL = import.meta.env.VITE_API_BASE_URL;

    this.client = axios.create({
      baseURL,
      timeout: 20000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include headers from localStorage
    this.client.interceptors.request.use((config) => {
      const brandId = localStorage.getItem(STORAGE_KEYS.BRAND_ID);
      const storeId = localStorage.getItem(STORAGE_KEYS.STORE_ID);
      const language = localStorage.getItem(STORAGE_KEYS.I18N_LANGUAGE) ?? 'en';

      if (brandId) {
        config.headers['x-brand-id'] = brandId;
      }
      if (storeId) {
        config.headers['x-store-id'] = storeId;
      }
      config.headers['x-language'] = language;

      return config;
    });
  }

  async getQrPaymentStatus(bookingCode: string): Promise<QrPaymentStatus> {
    const response = await this.client.get<ApiResponse<QrPaymentStatus>>(
      `/qr-payments/${bookingCode}/status`,
    );
    return response.data.data;
  }

  // Extract shop name from domain
  private getShopName(): string {
    const hostname = window.location.hostname;
    if (this.isTestHost(hostname)) return 'test';

    // Extract from *.booking.thaihandpos.com
    const parts = hostname.split('.');
    if (
      parts.length >= 3 &&
      (parts[1] === 'booking' || parts[1] === 'staging-booking') &&
      parts[2] === 'thaihandpos'
    ) {
      return parts[0];
    }

    // Fallback to default
    return 'test';
  }

  // Return true when hostname matches configured test host list.
  // Accepts exact hostnames (example.com) and suffix entries starting with '.' (.ngrok.io).
  private isTestHost(hostname: string): boolean {
    const testHostsRaw = import.meta.env.VITE_TEST_SHOP_HOSTS;
    const testHosts = (testHostsRaw ? testHostsRaw.split(',') : [])
      .map((host) => host.trim())
      .filter(Boolean);

    return testHosts.some((host) => {
      if (host.startsWith('.')) return hostname.endsWith(host);
      return hostname === host;
    });
  }

  setBrandId(brandId: number) {
    localStorage.setItem(STORAGE_KEYS.BRAND_ID, brandId.toString());
  }

  setStoreId(storeId: number) {
    localStorage.setItem(STORAGE_KEYS.STORE_ID, storeId.toString());
  }

  // API Methods
  async getBrandInfo(): Promise<Brand> {
    const shopName = this.getShopName();
    const response = await this.client.get<ApiResponse<Brand>>(
      `/brands/${shopName}`,
    );
    const brand = response.data.data;

    this.setBrandId(brand.brandId);
    return brand;
  }

  async checkAvailability(
    request: CheckAvailabilityRequest,
  ): Promise<Availability[]> {
    const response = await this.client.post<ApiResponse<Availability[]>>(
      '/availability',
      request,
    );
    return response.data.data;
  }

  async listTreatments(request: ListTreatmentsRequest): Promise<Treatment[]> {
    const response = await this.client.post<ApiResponse<Treatment[]>>(
      '/treatments/availability',
      request,
    );
    return response.data.data;
  }

  async addTreatment(
    request: ChooseTreatmentRequest,
  ): Promise<ChooseTreatmentResponse> {
    const response = await this.client.post<
      ApiResponse<ChooseTreatmentResponse>
    >('/treatments/confirm', request);
    return response.data.data;
  }

  async deleteTreatment(
    request: DeleteTreatmentRequest,
  ): Promise<DeleteTreatmentResponse> {
    const response = await this.client.delete<
      ApiResponse<DeleteTreatmentResponse>
    >('/treatments/confirm', { data: request });
    return response.data.data;
  }

  async listTherapists(): Promise<Therapist[]> {
    const response =
      await this.client.get<ApiResponse<Therapist[]>>('/therapists');
    return response.data.data;
  }

  async validateBooking(
    request: ValidateBookingRequest,
  ): Promise<ValidateBookingResponse> {
    const response = await this.client.post<
      ApiResponse<ValidateBookingResponse>
    >('/bookings/validate', request);
    return response.data.data;
  }

  async createBooking(
    request: CreateBookingRequest,
  ): Promise<CreateBookingResponse> {
    const response = await this.client.post<ApiResponse<CreateBookingResponse>>(
      '/bookings',
      request,
    );
    return response.data.data;
  }

  async getBooking(bookingCode: string): Promise<Booking> {
    const response = await this.client.get<ApiResponse<Booking>>(
      `/bookings/${bookingCode}`,
    );
    return response.data.data;
  }

  async requestOTP(
    _destination: string,
    _channel?: 'phoneNumber' | 'email',
  ): Promise<OTPResponse> {
    const response = await this.client.post<
      ApiResponse<{
        ref: string;
        expiresAt: string;
        code?: string;
      }>
    >('/otps', {
      destination: _destination,
      channel: _channel ?? 'phoneNumber',
    });

    const data = response.data.data;
    return {
      ...data,
      expiresAt: new Date(data.expiresAt),
    };
  }

  async checkAuthExists(telPrefix: string, tel: string): Promise<boolean> {
    const response = await this.client.post<ApiResponse<{ exists: boolean }>>(
      '/auth/exists',
      {
        telPrefix,
        tel,
      },
    );
    return response.data.data.exists;
  }

  async validateOTP(code: string, _ref: string): Promise<boolean> {
    const response = await this.client.post<ApiResponse<void>>(
      '/otps/validate',
      {
        code,
        ref: _ref,
      },
    );
    return response.data.success;
  }

  async getCustomerPackages(_phoneNumber: string): Promise<CustomerPackage[]> {
    const otpRef = localStorage.getItem(STORAGE_KEYS.OTP_REF) ?? undefined;
    const body: Record<string, string | undefined> = {
      phoneNumber: _phoneNumber,
    };
    if (otpRef) body.ref = otpRef;
    const response = await this.client.post<ApiResponse<CustomerPackage[]>>(
      '/customer-packages',
      body,
    );
    return response.data.data;
  }

  async getCustomerPackagesStatus(
    request: GetCustomerPackagesStatusRequest,
  ): Promise<CustomerPackageStatus> {
    const response = await this.client.post<ApiResponse<CustomerPackageStatus>>(
      '/customer-packages/status',
      request,
    );
    return response.data.data;
  }

  async validateCustomerPackages(
    _request: ValidateCustomerPackagesRequest,
  ): Promise<ValidatePackagesResponse> {
    const response = await this.client.post<
      ApiResponse<ValidatePackagesResponse>
    >('/customer-packages/validate', _request);
    return response.data.data;
  }
}

// Create singleton instance
export const apiClient = new BookingApiClient();
